<template>
  <BaseModal
    :id="id"
    class="activity-template-modal"
    hide-footer
    :initial-focus="() => (previousFocus || $refs.closeButton)"
    is-wide
    :focus-trap-active="!nestedModalIsOpen"
    :on-close="cancel"
  >
    <LoadingIndicator
      v-if="!activityTemplate"
      class="activity-template-modal__loading-indicator"
    />
    <div class="activity-template-modal__masthead">
      <button
        ref="closeButton"
        :aria-label="$t('Close')"
        class="activity-template-modal__close-button"
        @click="cancel"
      >
        <NebulaIcon symbol-id="x" />
      </button>
      <h2 class="activity-template-modal__heading">
        {{ decodedTitle }}
      </h2>
      <div class="activity-template-modal__meta-wrapper">
        <NebulaIcon
          class="activity-template-modal__meta-icon"
          symbol-id="sos"
        />
        <span class="activity-template-modal__meta">
          {{ $t('SOS Instructional Strategy') }}
        </span>
        <span class="activity-template-modal__meta">
          {{ draftType === 'board' ? $t('Board') : $t('Slideshow') }}
        </span>
        <span class="activity-template-modal__meta">
          {{ draftType === 'board' ? $t('Pages') : $t('Slides') }}
          <span class="activity-template-modal__badge">
            {{ draft && draft.pages ? draft.pages.length : '' }}
          </span>
        </span>
      </div>
      <CometBlockMessage
        v-if="error"
        block-message-type="error"
      >
        <p>{{ error }}</p>
      </CometBlockMessage>
      <div class="activity-template-modal__row">
        <div class="activity-template-modal__left-col">
          <ActivityTemplateModalPreview />
        </div>
        <div class="activity-template-modal__right-col activity-template-modal__actions-wrapper">
          <button
            class="activity-template-modal__build-button"
            @click="buildActivity"
          >
            <NebulaIcon
              symbol-id="plus"
              size="m"
            />
            <span>
              {{ $t('Build This Activity') }}
            </span>
          </button>
          <hr class="activity-template-modal__actions-divider">
          <button
            class="activity-template-modal__copy-link-button"
            @click="openCopyLinkModal"
          >
            <NebulaIcon
              symbol-id="link"
              size="m"
            />
            {{ $t('Copy Link') }}
          </button>
        </div>
      </div>
    </div>
    <div class="activity-template-modal__body">
      <div class="activity-template-modal__row">
        <div class="activity-template-modal__left-col">
          <h3 class="activity-template-modal__subheading">
            {{ $t('About') }}
          </h3>
          <p class="activity-template-modal__description">
            {{ activityTemplate ? activityTemplate.asset.description : '' }}
          </p>
          <template v-if="relatedAssets.length">
            <h3 class="activity-template-modal__subheading">
              {{ $t('More Educational Resources Like This') }}
            </h3>
            <NebulaCardCarousel
              ref="nebulaCarousel"
              carousel-name="related-assets"
              :adjust-height="120"
              :set-rtl="isRTL"
              :breakpoints="carouselBreakpoints"
              :extra-tracking-data="{ carouselTitle: 'Related Assets' }"
            >
              <ul class="nebula-card-row nebula-card-carousel__list">
                <component
                  :is="asset.asset_type.name === 'Channel'
                    ? 'NebulaChannelCard'
                    : 'NebulaAssetCard'"
                  class="nebula-card-carousel__list-item nebula-card"
                  v-for="asset in relatedAssets"
                  :key="asset.id"
                  :asset="asset"
                  :asset-type="asset.asset_type.name"
                  :url="`${domains.appDomain}/player/${asset.id}`"
                  @vue:mounted="initNebulaCarousel"
                >
                  <template #actionsMenu>
                    <!--
                      The data-asset-id here is used to control focus,
                      not as a prop
                    -->
                    <ActionMenu
                      :data-asset-id="asset.id"
                      :asset="asset"
                      :asset-type="asset.asset_type.name"
                      :back-url="backUrl"
                      :learn-url="domains.appDomain"
                      :user-is-student="userIsStudent"
                      :show-create-with-studio="false"
                      :quicklist-ids="quicklistIds"
                      @quicklist:remove="onRemoveFromQuicklist"
                      @quicklist:add="onAddToQuicklist"
                      @share="openShareModal(asset)"
                      @save="openMyContentModal(asset)"
                    />
                  </template>
                </component>
              </ul>
            </NebulaCardCarousel>
          </template>
        </div>
        <div class="activity-template-modal__right-col">
          <h3 class="activity-template-modal__subheading">
            {{ $t('Skills') }}
          </h3>
          <ul class="activity-template-modal__badge-list">
            <li
              v-for="skill in skills"
              :key="skill"
              class="activity-template-modal__badge"
            >
              {{ skill }}
            </li>
          </ul>
          <h3 class="activity-template-modal__subheading">
            {{ $t('Can Be Used With') }}
          </h3>
          <ul class="activity-template-modal__badge-list">
            <li
              v-for="label in canBeUsedWith"
              :key="label"
              class="activity-template-modal__badge"
            >
              {{ label }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import { ActionMenu } from '@discoveryedu/platform-components';
import {
  NebulaAssetCard,
  NebulaChannelCard,
  NebulaCardCarousel,
  NebulaIcon,
} from '@discoveryedu/nebula-components';
import { get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import ActivityTemplateModalPreview from '@/components/modals/ActivityTemplateModalPreview.vue';
import BaseModal from '@/components/modals/BaseModal.vue';
import CometBlockMessage from '@/components/ui-elements/CometBlockMessage.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import * as types from '@/lib/constants/store';
import { decodeHTML } from '@/lib/utils';
import {
  useEditorStore,
  useModalStore,
  useQuicklistStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'ActivityTemplateModal',
  components: {
    ActionMenu,
    ActivityTemplateModalPreview,
    BaseModal,
    CometBlockMessage,
    LoadingIndicator,
    NebulaAssetCard,
    NebulaChannelCard,
    NebulaCardCarousel,
    NebulaIcon,
  },
  data() {
    return {
      activityTemplate: null,
      error: null,
      isClosing: false,
      loading: false,
      // for keeping track of where to restore focus to when a submodal is closed
      previousFocus: null,
    };
  },
  props: {
    id: {
      type: String, // used for opening and closing modal
      required: true,
    },
    options: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'domains',
      'draft',
      'draftType',
    ]),
    ...mapState(useModalStore, [
      'modalIsOpen',
      'modals',
      'myContentModalOptions',
    ]),
    ...mapState(useUserStore, [
      'region',
      'user',
      'userIsStudent',
    ]),
    ...mapState(useQuicklistStore, [
      'quicklistIds',
    ]),
    canBeUsedWith() {
      if (!this.activityTemplate) return [];

      return this.activityTemplate.asset.tags
        .filter((tag) => tag.type === 'Most Relevant Media Types')
        .map((tag) => tag.name);
    },
    carouselBreakpoints() {
      return [
        {
          bleed: 16,
          name: 'tabletPortrait',
        },
        {
          bleed: 16,
          name: 'mobile',
        },
      ];
    },
    backUrl() {
      return window.location.href;
    },
    decodedTitle() {
      return decodeHTML(this.activityTemplate?.asset?.title);
    },
    isRTL() {
      // RTL is read from the user locale; default to LTR.
      return !get(this.user, 'analytics_data.locale.text_direction_is_ltr', true);
    },
    nestedModalIsOpen() {
      return this.modals.length > 1 || get(this, 'myContentModalOptions.config.isActive');
    },
    relatedAssets() {
      if (!this.activityTemplate) return [];

      return this.activityTemplate.related_assets
        .map((asset) => {
          // For TEAs, the human-readable type is the asset subtype
          const isTEA = asset.asset_type.id === 'a6d4de3c-8100-4fb5-b910-5cf311b51f1e';
          return {
            ...asset,
            asset_type: {
              ...asset.asset_type,
              name: isTEA ? asset.asset_subtype.name : asset.asset_type.name,
            },
          };
        });
    },
    skills() {
      if (!this.activityTemplate) return [];

      return this.activityTemplate.asset.tags
        .filter((tag) => tag.type === 'Thinking Skills')
        .map((tag) => tag.name);
    },
  },
  mounted() {
    if (!get(this, '$route.query.id')) return;

    this.$api.get(`/templates/activity/${this.$route.query.id}`).then((res) => {
      if (res && res.status === 200) {
        this.activityTemplate = res.data.template;

        if (this.$route.query.id !== res.data.template.asset.id) {
          // If we passed in a pointer id or an old revision id, rewrite the url
          // to make sure the preview loads correctly and the most recent id
          // gets shared
          this.$router.replace({
            query: {
              id: res.data.template.asset.id,
            },
          });
        }
      }
    });
  },
  methods: {
    ...mapActions(useModalStore, [
      types.CLOSE_MODAL,
      types.OPEN_MODAL,
      types.SET_MY_CONTENT_MODAL_OPTIONS,
    ]),
    ...mapActions(useEditorStore, [
      types.COPY_CREATION,
    ]),
    ...mapActions(useQuicklistStore, [
      types.ADD_TO_QUICKLIST,
      types.REMOVE_FROM_QUICKLIST,
    ]),
    async buildActivity() {
      this.error = null;
      this.loading = true;
      try {
        const newDraft = await this[types.COPY_CREATION]({
          type: 'creation',
          id: this.draft.id,
        });

        // New drafts always have a single reference
        const draftReference = newDraft.references[0];

        this.$router.push({
          path: '/edit',
          query: {
            id: draftReference.reference_id,
          },
          hash: '#notes-open',
        });
        this.isClosing = true;
        this[types.CLOSE_MODAL]({ id: this.id });
      } catch (e) {
        this.error = get(e, 'response.data.meta.message', e);
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      // Prevent duplicate calls
      if (!this.$route.query.id) return;

      // If the share modal is open, don't try to close the activity
      // template modal
      if (this.nestedModalIsOpen) return;

      if (this.$route.name === 'activity-templates' && !this.isClosing) {
        // Clear out any query params associated with the modal
        this.$router.push({
          query: {
            ...this.$route.query,
            id: undefined,
            page_id: undefined,
          },
        });
      }
    },
    initNebulaCarousel() {
      this.$refs.nebulaCarousel.init();
    },
    onAddToQuicklist(asset) {
      this[types.ADD_TO_QUICKLIST](asset);
    },
    onRemoveFromQuicklist(asset) {
      this[types.REMOVE_FROM_QUICKLIST](asset);
    },
    openCopyLinkModal(e) {
      this.previousFocus = e.target;

      this[types.OPEN_MODAL]({
        type: 'AssetShareModal',
        id: 'AssetShareModal',
        assetUrl: `${this.domains.studioDomain}/activity-templates?id=${this.activityTemplate.asset.id}`,
        assetGuid: this.activityTemplate.asset.id,
        headline: this.activityTemplate.asset.title,
        description: this.activityTemplate.asset.description || '',
      });
    },
    openMyContentModal(asset) {
      this.previousFocus = this.$el
        .querySelector(`.nebula-card__action-menu[data-asset-id=${asset.id}] .nebula-button`);

      this[types.SET_MY_CONTENT_MODAL_OPTIONS]({
        asset: {
          actions: [],
          selectedAssetId: asset.id,
          selectedAssetTitle: asset.title || asset.name,
          sharingEnabled: true,
        },
        config: {
          hideDistrictTab: this.region === 'UK', // see DEX-2646
          isActive: true,
          isStudent: this.userIsStudent,
          modalType: 'save',
        },
      });
    },
    openShareModal(asset) {
      this.previousFocus = this.$el
        .querySelector(`.nebula-card__action-menu[data-asset-id=${asset.id}] .nebula-button`);

      this[types.OPEN_MODAL]({
        type: 'AssetShareModal',
        id: 'AssetShareModal',
        assetUrl: `${this.domains.appDomain}/player/${asset.id}`,
        assetGuid: asset.id,
        headline: asset.name,
        description: asset.description || '',
      });
    },
  },
  watch: {
    '$route.query': function _({ id }) {
      // If we navigate away, close the modal
      if (!id) this[types.CLOSE_MODAL]({ id: this.id });
    },
  },
};
</script>

<style lang="stylus">
.activity-template-modal {
  // Style overrides
  .comet-modal {
    border-radius: $nebula-border-radius-button-default;
    overflow-y: auto;
    width: max-content;

    .comet-modal__inner {
      border-radius: $nebula-border-radius-button-default;
      padding: 0;
    }
  }

  &__loading-indicator {
    background-color: $nebula-color-white;
  }

  &__masthead {
    background-color: $nebula-color-neutral-200;
    border-radius: $nebula-border-radius-button-default $nebula-border-radius-button-default 0 0;
    max-width: 100%;
    padding: $nebula-space-4x;
    width: 100vw;
  }

  &__close-button {
    background: none;
    border: 1px solid $nebula-color-interface-blue-300;
    border-radius: 50%;
    padding: $nebula-space-1x;
    position: absolute;
    inset-inline-end: $nebula-space-4x;
    top: $nebula-space-4x;

    svg {
      display: block;
      fill: $nebula-color-interface-blue-600;
      height: $comet-size-icon-m;
      width: $comet-size-icon-m;
    }
  }

  &__heading {
    nebula-text-h4();
    margin: 0 0 $nebula-space-half;
    padding-inline-end: $nebula-space-7x;
  }

  &__meta-wrapper {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    margin-bottom: $nebula-space-4x;
  }

  &__meta-icon {
    align-self: center;
    margin-inline-end: $nebula-space-1x;
  }

  &__meta {
    nebula-text-body-2();
    color: $nebula-color-neutral-500;
    font-weight: bold;
    line-height: 21px; // align meta text with badges
    margin-inline-end: $nebula-space-2x;
  }

  &__row {
    align-items: flex-start;
    display: flex;
    flex-wrap: nowrap;
  }

  &__left-col {
    flex-grow: 0;
    flex-shrink: 0;
    padding-inline-end: $nebula-space-2x;
    width: 70%;
  }

  &__right-col {
    flex-grow: 1;
    flex-shrink: 1;
  }

  &__actions-wrapper {
    border: 1px solid $nebula-color-interface-blue-200;
    border-radius: $nebula-border-radius-button-default;
    padding: $nebula-space-2x;
  }

  &__build-button {
    align-items: center;
    background-color: $nebula-color-interactive-blue-300;
    border: 0;
    border-radius: $nebula-border-radius-button-default;
    color: $nebula-color-white;
    display: flex;
    font-size: $nebula-font-size-body-1;
    justify-content: flex-start;
    line-height: 1.4;
    margin: 0;
    padding: $nebula-space-2x $nebula-space-3x;
    transition: $nebula-transition-default;
    width: 100%;

    svg {
      fill: $nebula-color-white;
      flex-shrink: 0;
      height: $comet-size-icon-m;
      margin-inline-end: $nebula-space-1x;
      width: $comet-size-icon-m;
    }

    &:hover,
    &:focus {
      background: $nebula-color-interactive-blue-400;
    }

    &:active {
      background: $nebula-color-interactive-blue-500;
    }
  }

  &__copy-link-button {
    align-items: center;
    background: none;
    border: 0;
    border-radius: $nebula-border-radius-button-default;
    color: $nebula-color-interactive-blue-500;
    display: flex;
    font-size: $nebula-font-size-body-1;
    justify-content: flex-start;
    line-height: 1.4;
    margin: 0;
    padding: $nebula-space-2x $nebula-space-3x;
    transition: $nebula-transition-default;
    width: 100%;

    svg {
      fill: $nebula-color-interactive-blue-500;
      flex-shrink: 0;
      height: $comet-size-icon-m;
      margin-inline-end: $nebula-space-1x;
      width: $comet-size-icon-m;
    }

    &:hover,
    &:focus {
      background-color: rgba($nebula-color-interactive-blue-500, 0.25);
    }
  }

  &__actions-divider {
    border: 0;
    border-bottom: 1px solid $nebula-color-interface-blue-200;
    margin: $nebula-space-2x $nebula-space-2x * -1 $nebula-space-2x;
  }

  &__body {
    padding: $nebula-space-4x $nebula-space-4x 0;
  }

  &__subheading {
    nebula-text-body-1();
    color: $nebula-color-neutral-500;
    font-weight: bold;
    margin: 0 0 $nebula-space-1x;
  }

  &__description {
    nebula-text-body-1();
    margin: 0 0 $nebula-space-4x;
  }

  &__badge-list {
    margin: 0 0 $nebula-space-3x;
    padding: 0;
  }

  &__badge {
    body-base();
    background-color: $nebula-color-interface-blue-100;
    border-radius: 2px;
    color: $nebula-color-interface-blue-600;
    display: inline-block;
    padding: 2px $nebula-space-1x;
    white-space: nowrap;

    .activity-template-modal__badge-list & {
      margin-bottom: $nebula-space-1x;
    }

    &:not(:last-child) {
      margin-inline-end: $nebula-space-1x;
    }

    .activity-template-modal__meta & {
      margin-inline-start: $nebula-space-1x;
    }
  }
}
</style>
